import { useCallback, useEffect, useState } from 'react';
import { getFinanceSummary } from '../lib/api/endpoints';
import { financeSummaryFromDto } from '../lib/api/mappers';
import type { FinanceGroupBy, FinanceSummary } from '../types/finance';

export interface FinanceAnalyticsQuery {
  from: string;
  to: string;
  subunitId?: string;
  vehicleId?: string;
  customerId?: string;
}

const EJES: FinanceGroupBy[] = ['period', 'vehicle', 'subunit', 'category', 'customer'];

export type FinanceAnalytics = Record<FinanceGroupBy, FinanceSummary>;

/**
 * FE-32 — Finanzas analítica: los CINCO desgloses de `/finance/summary` para
 * el mismo rango y los mismos filtros, en paralelo. Son agregados (ADR-016):
 * cinco lecturas de decenas de filas cada una, no cinco barridos de crudas.
 * Los totales son idénticos en los cinco (misma base); la página los toma del
 * primero.
 */
export function useFinanceAnalytics(query: FinanceAnalyticsQuery) {
  const [data, setData] = useState<FinanceAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const queryKey = JSON.stringify(query);

  useEffect(() => {
    const parsed = JSON.parse(queryKey) as FinanceAnalyticsQuery;
    let cancelado = false;
    setIsLoading(true);
    setError(null);
    Promise.all(EJES.map(groupBy => getFinanceSummary({ ...parsed, groupBy }).then(financeSummaryFromDto)))
      .then(res => {
        if (cancelado) return;
        setData(Object.fromEntries(EJES.map((e, i) => [e, res[i]])) as FinanceAnalytics);
      })
      .catch(err => { if (!cancelado) setError(err instanceof Error ? err : new Error(String(err))); })
      .finally(() => { if (!cancelado) setIsLoading(false); });
    return () => { cancelado = true; };
  }, [queryKey, reloadToken]);

  const reload = useCallback(() => setReloadToken(t => t + 1), []);
  return { data, isLoading, error, reload };
}
